import { useState, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { EventPageHeading } from 'src/modules/events/components/EventPage/EventPageHeading';
import { Showcase } from 'src/modules/home/components/HomeBusinessCasesSection/Showcase';
import { ModalPortal } from 'src/components/common/ModalPortal';
import { EventPageSpeakerModal } from 'src/modules/events/components/EventPage/EventPageSpeakersSection/EventPageSpeakerModal';
import { AnimatePresence } from 'framer-motion';
import { EventPageContext } from 'src/modules/events/components/EventPage/EventPageContext';
import { People } from 'src/graphql/schema.graphql';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';

const Section = styled.section`
  padding: 41px 0 20px;
  ${media.w.greaterThanOrEqual.desktop} {
    padding: 64px 0 30px;
  }
`;

const Container = styled(ConstraintContainer)`
  max-width: 1020px;
`;

const SpeakersContainer = styled(ConstraintContainer)`
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(2, 1fr);
  max-width: 1170px;
  padding-top: 30px;
  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: repeat(4, 1fr);
    padding-top: 40px;
  }
  ${media.w.lessThan.desktop} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const SpeakerShowcase = styled(Showcase).attrs({ forwardedAs: 'div' })`
  &&& {
    height: 286px;
    margin: 0;
    cursor: pointer;
    & > div {
      width: 100%;
    }
    ${media.w.greaterThanOrEqual.desktop} {
      height: 420px;
      &:nth-child(4n - 1) {
        top: -60px;
      }
      &:nth-child(4n - 2) {
        top: 60px;
      }
      &:nth-child(4n) {
        top: 40px;
      }
      padding-bottom: 30px;
      h4 + div {
        line-height: 20px;
      }
    }
    ${media.w.lessThan.desktop} {
      &:nth-child(2n) {
        top: 20px;
      }
      h4 {
        font-size: 18px;
        line-height: 21px;
      }
      h4 + div {
        font-size: 12px;
        line-height: 16px;
        margin-top: 4px;
      }
    }
  }
`;

export const EventPageSpeakersSection = () => {
  const { event } = useContext(EventPageContext);

  const [activeSpeaker, setActiveSpeaker] = useState<People | null>(null);
  const translateItem = useTranslateItem();
  const { _ } = useTranslate();

  const preparedSpeakers = useMemo(
    () =>
      event.speakers.map((speaker) => translateItem(speaker.people_id)) || [],
    [event, translateItem],
  );

  if (preparedSpeakers.length === 0) {
    return null;
  }

  return (
    <Section>
      <Container>
        <EventPageHeading>{_('speakers')}</EventPageHeading>
      </Container>
      <SpeakersContainer>
        {preparedSpeakers.map((speaker, i) => (
          <SpeakerShowcase
            key={i}
            image={speaker.portrait?.id}
            title={speaker.name}
            subtitle={[speaker.degree, speaker.title]
              .filter((el) => el)
              .join(', ')}
            onClick={() => setActiveSpeaker(speaker)}
            withChevron
          />
        ))}
      </SpeakersContainer>
      <ModalPortal open={!!activeSpeaker}>
        <AnimatePresence exitBeforeEnter>
          {activeSpeaker && (
            <EventPageSpeakerModal
              speaker={activeSpeaker}
              close={() => setActiveSpeaker(null)}
            />
          )}
        </AnimatePresence>
      </ModalPortal>
    </Section>
  );
};
